import {reactive} from 'vue';
import {getCollection} from '@/lib/filters/filters';
import {
  addSubcollectionDocument,
  getDocument,
  currentUser,
  deleteSubcollectionDocument,
  updateDocument,
  updateSubcollectionDocument,
  addDocument,
  getSubcollection,
} from '@/services/FirestoreService';
import {Feature} from '@/services/OverlaysService/Feature';
import {MapService, gmAPI} from '@/services/MapService';
import {getStore} from '@/services/store';
import {insertAuditRecord} from '@/services/BackendService';
import {geohashForLocation} from 'geofire-common';
import {FeatureManager} from '../FeatureManager/FeatureManager';
import {FEATURES_COLLECTION, SHAREDMAPS_COLLECTION} from '@/lib/constants';
import {compressObjectToBase64, decompressBase64ToObject} from '@northpoint-development/labs-common-core';

/**
 * @typedef {"link"|"restricted"} AccessType
 * @typedef {"view"|"edit"} AccessLevel
 *
 * @typedef {Object} AccessLogEntry
 * @property {Date} timestamp
 * @property {string} [uid]
 * @property {"internal"|"external"} [source]
 * @property {string} [ip]
 * @property {string} [userAgent]
 * @property {string} [url]
 * @property {"view"|"edit"} [mode]
 * @property {boolean} [authorized]
 *
 * @typedef {Object} UsageStats
 * @property {number} internalViews The number of times the map has been viewed by users with an internal employee
 * account.
 * @property {number} externalViews The number of times the map has been viewed by users without an internal employee
 * account.
 *
 *
 * @typedef {Object} ShareSettings
 * @property {AccessType} accessType
 * @property {AccessLevel} accessLevel
 * @property {string[]} users
 *
 * @typedef {Object} UserAction
 * @property {string} uid
 * @property {string} name
 * @property {Date} timestamp
 *
 * @typedef {import('@/services/OverlaysService').Feature} Feature
 *
 * @typedef {google.maps.LatLngBounds|google.maps.LatLngBoundsLiteral} Bounds
 *
 * @typedef {import('@/lib/filters/subscriptions').SubscriptionEvent} SubscriptionEvent
 *
 * Options for creating a SharedMap.
 * @typedef {Object} SharedMapOptions - The options object.
 * @property {string} id - The ID of the shared map.
 * @property {string} name - The name of the shared map.
 * @property {Bounds} bounds - The bounds of the shared map.
 * @property {Date|null} deleted - Whether the shared map is deleted or not.
 * @property {import('../FeatureManager/types').Folder[]} folders - The folders of the shared map.
 * @property {{url: string}} [image] - The image of the shared map.
 * @property {number} schemaVersion - The schema version of the shared map.
 * @property {UsageStats} usageStats - The usage statistics of the shared map.
 * @property {ShareSettings} share - The share information of the shared map.
 * @property {UserAction} createdOn - The creation date of the shared map.
 * @property {UserAction} [editedOn] - The date the shared map was edited.
 * @property {UserAction} [deletedOn] - The date the shared map was deleted.
 * @property {Feature[]} [features] - The features of the shared map.
 * @property {AccessLogEntry[]} [accessLog] - The access log of the shared map.
 */

/**
 * Private helper to check if a feature should not be rendered.
 * @param {Feature} feature feature to check
 * @return {boolean} true if the feature is in the current map viewport
 */
function _featureInRange(feature) {
  const radius = MapService.getMapRadius();
  const mapCenter = MapService.getMap().getCenter();
  return feature.isInRange(mapCenter, radius);
}

/**
 * Flattens out a folder array to return a one dimensional array of
 * all features contained within the folder structure
 * @param {Array} arr - The array of nested folders to flatten
 * @param {number[]} path - The array to flatten
 * @return {Map<string, number[]>} A map whose keys are feature IDs and values are the
 * path to that feature within the folders structure
 */
function _flattenFolderFeatures(arr) {
  const output = new Map();
  function flatten(arr, path = []) {
    return arr.reduce((acc, curr, index) => {
      if (curr.id) {
        acc.push({id: curr.id, path: path.concat(index)});
      }
      if (curr.children) {
        acc = acc.concat(flatten(curr.children, path.concat(index)));
      }
      return acc;
    }, []);
  }
  flatten(arr).map((i) => output.set(i.id, i.path));

  return output;
}

/**
 * Represents a shared map.
 */
class SharedMap extends FeatureManager {
  /**
   * Creates a new instance of SharedMap.
   * @param {SharedMapOptions} options
   */
  constructor(options) {
    super(options);
    this.render = true;

    this.collectionName = SHAREDMAPS_COLLECTION;
    this.featureCollectionName = FEATURES_COLLECTION;

    // #region Unique SharedMap properties
    /** @type {UserAction} The creation date of the shared map. */
    this.createdOn = options.createdOn;
    /** @type {UserAction} The date the shared map was edited. */
    this.editedOn = options.editedOn;
    /** @type {UserAction} The date the shared map was deleted. */
    this.deletedOn = options.deletedOn;
    /** @type {Bounds} The bounds of the shared map. */
    this.bounds = options.bounds;
    /** @type {{url: string}} The image of the shared map. Not defined if the map has no generated image. */
    this.image = options.image;
    /** @type {boolean} Whether the shared map is deleted or not. */
    this.deleted = options.deleted;

    /** @type {number} The schema version of the shared map. */
    this.schemaVersion = options.schemaVersion;
    /** @type {UsageStats} The usage statistics of the shared map. */
    this.usageStats = options.usageStats;
    /** @type {ShareSettings} The share information of the shared map. */
    this.share = options.share;

    // The following are stored in a subcollection and may be partially loaded or not present at all, depending on the
    // query.
    /** @type {AccessLogEntry[]} The access log of the shared map. */
    this.accessLog = options.accessLog;
    /** @type {boolean} Indicates whether or not a warning email has been sent for making this map publicly viewable */
    this.hasSentWarningEmail = options.hasSentWarningEmail ?? false;
    // #endregion

    let folderFeatures;
    if (this.folders) {
      folderFeatures = _flattenFolderFeatures(this.folders);
    }
    if (options.features instanceof Map) {
      const featuresCollection = getCollection('features');
      // the features may have already been added to the features collection by geohash, so we need to check. Build a
      // list and add to the collection once to minimize subscriber updates.
      const toAdd = [];
      for (const [fid, f] of options.features) {
        if (featuresCollection.sourceHasId(f.id)) {
          const existingFeature = featuresCollection.getItem(fid);
          if (existingFeature) {
            this.features.set(fid, existingFeature);
          }
        } else {
          try {
            const feature = new Feature(f, this);
            this.features.set(feature.id, feature);
            toAdd.push(feature);
          } catch (e) {
            console.error(`Error creating feature for sharedMap ${this.name} (${this.id})`, e);
          }
        }
        // Push the feature to the root of folders if it doesn't exist
        if (!folderFeatures.has(f.id)) {
          this.folders.push({id: f.id});
        }
      }
      if (toAdd.length > 0) {
        featuresCollection.addItems(...toAdd);
      }
    }

    this.updateFeatureRenderOrder();

    // Making this object reactive enables watchers to work correctly
    const reactiveThis = reactive(this);
    return reactiveThis;
  }

  /**
   * Applies updated data to this sharedMap and persists it to the database. To only force an update of the editor and
   * timestamp, pass `{}` as the data. While this function is executing, the `editedAt` field will be `null`.
   * @param {OverlayData} data The updated data to apply to this sharedMap.
   * @param {boolean} updateEditedBy true when we want to update who it was edited by, false
   * @return {Promise<SharedMap>} A promise that resolves to this sharedMap.
   */
  async update(data, updateEditedBy = true) {
    // as soon as the update begins, indicate that the current data has not yet been saved by setting the editedAt
    // value to null.
    const originalEditedOn = this.editedOn;
    this.editedOn = null;

    // anything other than an object (including when no args are passed at all) is treated as an empty object
    if (!data || typeof data !== 'object') {
      data = {};
    }

    // Only valid fields for an sharedMap should be set in the database, so filter out anything that isn't a property
    // of `this`.
    const applyChanges = {};
    Object.entries(data).forEach(([key, value]) => {
      if (Object.hasOwn(this, key)) {
        applyChanges[key] = value;
      }
    });
    // some properties are not stored in the database, so remove them before updating
    delete applyChanges.features;
    delete applyChanges.id;
    delete applyChanges.editMode;

    if (updateEditedBy) {
      // any time the document is updated, even if there are no changes, we need to update the editor and timestamp
      applyChanges.editedOn = {
        name: currentUser().displayName,
        uid: currentUser().uid,
        timestamp: Date.now(),
      };
    } else {
      applyChanges.editedOn = originalEditedOn;
    }

    // Do not apply changes to this until the database call has succeeded. Consumers may monitor this.editedAt to
    // determine if saving is in progress.
    await updateDocument(SHAREDMAPS_COLLECTION, this.id, {
      ...applyChanges,
      // Not ideal to always update folders, but we would otherwise need to compress, save, and then decompress before
      // applying the changes to 'this'.
      folders: await compressObjectToBase64(applyChanges.folders ?? this.folders),
    });

    Object.assign(this, applyChanges);
    this.updateFeatureRenderOrder();
    return this;
  }

  /**
   * Sets the render status of all the features of this sharedMap. If the sharedMap is not visible, this will
   * do nothing. Otherwise, the render status will be set to the value of the `render` parameter.
   * @param {boolean} render Indicates whether all the features of this sharedMap should be rendered or not.
   */
  setRender(render) {
    this.render = render;
    if (!this.visible) return;
    for (const feature of this.features.values()) {
      if (_featureInRange(feature)) {
        feature.setRender(render);
      }
    }
  }

  /**
   * newFeature creates a new feature for this sharedMap and saves it to the database.
   * @param {Object} data The feature to add to this sharedMap.
   * @param {string} featureId - An optional document ID that a feature should use
   * @param {number[]} folderPath - Whether or not to restore the feature to its folder
   * @param {boolean} [fromRestoredFolder] - Special flag if being called from the folder restore function.
   * This is used to prevent pushing a "new feature" from the restore function to the root of folders unnecessarily.
   * @return {Promise<Feature>} A promise that resolves to the newly created feature.
   */
  async newFeature(data, featureId = null, folderPath = 0, fromRestoredFolder = false) {
    try {
      getStore().commit('setLoadingNewFeature', true);
      // make a new variable validData for if we are undoing deletion of a feature with infoWindowContent
      const validData = data;
      // If the data has infoWindowContent (basically whenever we undo delete),
      // Get rid of it before we undo deleting the feature
      if (data.infoWindowContent) {
        delete validData.infoWindowContent;
      }
      if (data?.events) {
        delete validData.events;
      }
      // Convert custom displayField objects to normal objects for saving to Firestore
      if (data?.displayFields?.length) {
        validData.displayFields = data.displayFields.map((displayField) => JSON.parse(JSON.stringify(displayField)));
      }
      // Convert custom comps objects to normal objects for saving to Firestore
      if (data?.comps?.length) {
        validData.comps = data.comps.map((comp) => JSON.parse(JSON.stringify(comp)));
      }
      // For a new feature, we must create the Firestore doc to get featureId before attempting to create the Feature.
      // In that case we'll have to undo the creation of the Firestore doc if creating the Feature fails.
      const isNew = !featureId;

      if (isNew) {
        featureId = await addSubcollectionDocument(SHAREDMAPS_COLLECTION, this.id, FEATURES_COLLECTION, validData);
      }
      let feature;
      try {
        feature = new Feature({...validData, id: featureId}, this);
      } catch (err) {
        console.error(`SharedMap.newFeature failed to add new feature to sharedMap ${this.name}: ${err}`);
        // Undo the creation of the feature in Firestore.
        if (isNew) {
          await deleteSubcollectionDocument(SHAREDMAPS_COLLECTION, this.id, FEATURES_COLLECTION, featureId);
        }
        getStore().commit('setLoadingNewFeature', false);
        return;
      }

      if (!isNew) {
        await updateSubcollectionDocument(SHAREDMAPS_COLLECTION, this.id, FEATURES_COLLECTION, featureId, validData);
      }
      if (folderPath) {
        const targetFolder = this.getFolderItem(folderPath);
        const folderToRestore = targetFolder?.parentFolder ? targetFolder.parentFolder : this.folders;
        this.addFolderItem(folderToRestore, {id: feature.id}, folderPath[folderPath.length - 1]);
      } else if (fromRestoredFolder === false) {
        this.folders.unshift({id: feature.id});
      }
      // Toolbox features are handled separately. This logic is here instead of in Toolbox.vue because when undoing
      // deletion, the restore function doesn't have access to anything in Toolbox.vue. If the toolBoxMinZoom is not
      // null, the feature is a toolbox feature
      // TODO come up with a better architecture for this
      if (feature.toolBoxMinZoom !== null) {
        feature.addHandler('click', 'toolboxSetEditing', async (event) => await feature.setEditing(true, {event}));
      }

      this.features.set(feature.id, feature);
      getCollection('features').addItems(feature);
      await this.update({folders: this.folders}); // force editor and timestamp to be updated

      // add edit and highlight event handlers to newly created feature
      feature.addEditListeners();
      feature.addHighlightListeners();
      getStore().commit('setLoadingNewFeature', false);
      return feature;
    } catch (error) {
      console.error('SharedMap.newFeature failed to add new feature to sharedMap', error);
      getStore().commit('setLoadingNewFeature', false);
    }
  }

  /**
   * addFeature adds an existing feature to this sharedMap object for tracking and control. This does NOT create a new
   * feature or modify this sharedMap.
   * @param {Object} data The feature data to add to this sharedMap.
   * @param {boolean} [skipRenderOrder] - Whether or not to skip updating the feature render order
   * @return {Feature} A promise that resolves to the newly created feature.
   */
  addFeature(data, skipRenderOrder = false) {
    let feature;
    try {
      feature = new Feature(data, this);
    } catch (err) {
      console.error(`SharedMap.addFeature failed to add feature to sharedMap "${this.name}"`, err);
      return; // Can't proceed with adding the feature
    }

    // add edit and highlight event handlers to newly created feature
    if (this.editMode) {
      feature.addEditListeners();
    } else {
      feature.addViewListeners();
    }
    feature.addHighlightListeners();

    const folderFeatures = _flattenFolderFeatures(this.folders);

    this.features.set(feature.id, feature);

    // Push the feature to the root of folders if it doesn't exist
    if (!folderFeatures.has(feature.id)) {
      this.folders.push({id: feature.id});
    }

    if (!skipRenderOrder) {
      this.updateFeatureRenderOrder();
    }
    return feature;
  }

  /**
   * Add features to the shared map and then add them to the features collection
   * @param {Feature[]} features - The features to add to the shared map.
   * @return {Promise<void>}
   */
  async initializeFeatures(features) {
    try {
      const newFeatures = [];
      for (let i = 0; i < features.length; i++) {
        const feature = features[i];
        if (this.features.has(feature.id)) continue;
        const addedFeature = this.addFeature(feature, true);
        newFeatures.push(addedFeature);
      }
      this.updateFeatureRenderOrder();

      if (newFeatures.length > 0) {
        getCollection('features').addItems(...newFeatures);
      }
    } catch (error) {
      error.message = `Error getting features by shared map: ${error.message}`;
      console.error(error);
    }
  }

  /**
   * Duplicates a feature in the shared map. Only works for placeMarker features.
   * @param {SubscriptionEvent} event - The event object.
   * @return {Promise<Feature>}
   */
  async duplicateFeature(event) {
    const feature = this.features.get(event.payload?.featureId);
    if (feature.featureType !== 'placeMarker') return;

    const screenPixelLocation = MapService.latLngToScreenPixel(feature.center);
    const location = MapService.screenPixelToLatLng(screenPixelLocation.x - 35, screenPixelLocation.y);
    const bounds = {
      north: location.lat(),
      south: location.lat(),
      east: location.lng(),
      west: location.lng(),
    };
    const geohash = geohashForLocation([(bounds.north + bounds.south) / 2, (bounds.east + bounds.west) / 2]);

    const newFeatureData = {
      comps: feature.comps,
      displayFields: feature.displayFields,
      featureType: feature.featureType,
      name: `COPY - ${feature.name ? feature.name : 'Marker'}`,
      geohash,
      bounds,
      graphicData: {
        bounds,
        featureType: 'placeMarker',
        clickable: true,
        icon: feature.icon.name,
      },
      visible: true,
    };

    const newFeature = await this.newFeature(newFeatureData);

    await getStore().dispatch('infowindow/closeInfoWindow');
    newFeature.openInfoWindow({
      payload: {
        toolbar: {toolbarOptions: []},
      },
    });

    return newFeature;
  }

  /** Updates the local feature and local features collection with the latest data in firestore */
  async refreshFeatures() {
    getStore().dispatch('infowindow/closeInfoWindow');

    const refreshedFeatures = await getSubcollection('sharedMaps', this.id, 'features', true);
    const featuresCollection = getCollection('features');
    for (const f of refreshedFeatures) {
      const feature = new Feature(f, this);
      this.features.set(feature.id, feature);
      featuresCollection.addItems(feature);
      feature.addEditListeners();
      feature.addHighlightListeners();
      feature.setVisibility(true);
    }

    await this.update({folders: this.folders}); // force editor and timestamp to be updated
  }
}

/**
 * Creates a new shared map with default options.
 * @return {Promise<SharedMap>}
 */
const createNewSharedMap = async () => {
  // Bounding box for continental united states
  const bounds = {
    north: '49.384358',
    south: '24.396308',
    east: '-66.885444',
    west: '-124.848974',
  };

  // Current user
  const user = currentUser();

  // Default options for a new shared map
  const newMapOptions = {
    name: 'Untitled Map',
    bounds,
    deleted: null,
    folders: [],
    schemaVersion: 1,
    usageStats: {
      internalViews: 0,
      externalViews: 0,
    },
    share: {
      accessType: 'restricted',
      accessLevel: 'edit',
      users: [],
    },
    createdOn: {
      timestamp: Date.now(),
      uid: user.uid,
      name: user.displayName,
    },
    editedOn: {
      timestamp: Date.now(),
      uid: user.uid,
      name: user.displayName,
    },
  };

  // Create the sharedMap in the firestore and retrieve the id
  newMapOptions.id = await addDocument('sharedMaps', {
    ...newMapOptions,
    folders: await compressObjectToBase64(newMapOptions.folders),
  });

  // Update the bounds to be a google maps bounds object
  newMapOptions.bounds = new gmAPI.LatLngBounds(
    new gmAPI.LatLng(bounds.south, bounds.west),
    new gmAPI.LatLng(bounds.north, bounds.east),
  );

  // Create the SharedMap instance with the updated mapOptions
  return new SharedMap(newMapOptions);
};

const duplicateSharedMap = async (id) => {
  // Get Original Map Data
  const sharedMapData = await getDocument('sharedMaps', id);

  // Create Duplicate Data and Add To Firestore
  const duplicateMapData = {
    name: `COPY - ${sharedMapData.name}`,
    bounds: sharedMapData.bounds,
    folders: sharedMapData.folders,
    deleted: null,
    schemaVersion: 1,
    loading: true,
    usageStats: {
      internalViews: 0,
      externalViews: 0,
    },
    share: {
      accessType: 'restricted',
      accessLevel: 'edit',
      users: [],
    },
    createdOn: {
      timestamp: Date.now(),
      uid: currentUser().uid,
      name: currentUser().displayName,
    },
    editedOn: {
      timestamp: Date.now(),
      uid: currentUser().uid,
      name: currentUser().displayName,
    },
    duplicatedFrom: id,
  };
  const duplicateId = await addDocument('sharedMaps', duplicateMapData);

  // Insert audit record for duplication; a failed write is logged, never holds up the duplicate.
  insertAuditRecord({
    eventPayload: {newMapId: duplicateId},
    eventType: 'duplicated',
    tilesFetched: 0,
    mapCreatorId: currentUser().uid,
    mapId: id,
    mapName: sharedMapData.name,
  }).catch((error) => {
    console.error('Error creating audit record:', error);
  });

  // Get Original Features and Add To Duplicate
  const sharedMapFeatures = await getSubcollection('sharedMaps', id, 'features', true);
  for (const feature of sharedMapFeatures) {
    feature.parent = duplicateId;
    await addSubcollectionDocument('sharedMaps', duplicateId, 'features', feature, feature.id);
  }
  // Features are loaded now so the tile is enabled
  duplicateMapData.loading = false;

  return {
    duplicateId,
    duplicatedMapData: {
      ...duplicateMapData,
      // 'folders' was assigned the value of the original map's compressed 'folders' string, so we decompress it before
      // use throughout the app.
      folders: await decompressBase64ToObject(duplicateMapData.folders),
    },
  };
};

export {
  // SharedMap class
  SharedMap,

  // Creation functions
  createNewSharedMap,
  duplicateSharedMap,
};
